// src/pages/registeredUsers/tabs/Overview.tsx
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faTags,
  faBox,
  faCheck,
  faMoneyBillWave,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';
import {
  ContentSection,
  SectionHeader,
  DashboardStatsContainer,
  StatCard,
  StatIcon,
  StatValue,
  StatLabel,
  Divider,
  ListingItem,
  ListingImage,
  ListingDetails,
  ListingTitle,
  ListingMeta,
  StatusBadge,
} from '../../pages/registeredUsers/styles/listings.style';
import { Listing } from '../../types/listing';

interface OverviewProps {
  totalListings: number;
  soldListings: number;
  availableListings: number;
  totalRevenue: number;
  listings: Listing[];
  stallDetails: any;
}

const Overview: React.FC<OverviewProps> = ({
  totalListings,
  soldListings,
  availableListings,
  totalRevenue,
  listings,
  stallDetails,
}) => {
  // Calculate remaining days
  const calculateRemainingDays = () => {
    if (!stallDetails || !stallDetails.createdAt || !stallDetails.subscriptionDuration) return 'N/A';
    const createdAt = stallDetails.createdAt.toDate ? stallDetails.createdAt.toDate() : new Date(stallDetails.createdAt);
    const duration = stallDetails.subscriptionDuration;
    let expiryDate = new Date(createdAt);

    if (duration === 'Weekly') {
      expiryDate.setDate(expiryDate.getDate() + 7);
    } else if (duration === 'Monthly') {
      expiryDate.setMonth(expiryDate.getMonth() + 1);
    } else if (duration === '6-Monthly') {
      expiryDate.setMonth(expiryDate.getMonth() + 6);
    } else if (duration === '12-Monthly') {
      expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    } else {
      return 'N/A';
    }

    const currentDate = new Date();
    const diffTime = expiryDate.getTime() - currentDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 'Expired';
  };

  const remainingDays = calculateRemainingDays();

  return (
    <ContentSection>
      <SectionHeader>
        <h2>
          <FontAwesomeIcon icon={faChartLine} /> Overview
        </h2>
        <p>Key metrics and statistics about your listings</p>
      </SectionHeader>

      <DashboardStatsContainer>
        <StatCard>
          <StatIcon>
            <FontAwesomeIcon icon={faTags} />
          </StatIcon>
          <StatValue>{totalListings}</StatValue>
          <StatLabel>Total Listings</StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon>
            <FontAwesomeIcon icon={faBox} />
          </StatIcon>
          <StatValue>{availableListings}</StatValue>
          <StatLabel>Available</StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon>
            <FontAwesomeIcon icon={faCheck} />
          </StatIcon>
          <StatValue>{soldListings}</StatValue>
          <StatLabel>Sold</StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon>
            <FontAwesomeIcon icon={faMoneyBillWave} />
          </StatIcon>
          <StatValue>UGX {totalRevenue.toLocaleString()}</StatValue>
          <StatLabel>Total Revenue</StatLabel>
        </StatCard>

        <StatCard>
          <StatIcon>
            <FontAwesomeIcon icon={faCalendarAlt} />
          </StatIcon>
          <StatValue>{remainingDays}</StatValue>
          <StatLabel>Days Remaining</StatLabel>
        </StatCard>
      </DashboardStatsContainer>

      <Divider />

      <SectionHeader>
        <h3>Recent Activity</h3>
      </SectionHeader>

      {listings.slice(0, 3).map((listing) => (
        <ListingItem key={listing.id} variant="compact">
          <ListingImage>
            {listing.mediaDetails && listing.mediaDetails.length > 0 ? (
              listing.mediaDetails[0].type === 'image' ? (
                <img src={listing.mediaDetails[0].url} alt={listing.title} />
              ) : (
                <video src={listing.mediaDetails[0].url} />
              )
            ) : (
              <img
                src={`https://placehold.co/100x70/cccccc/333333?text=No+Media`}
                alt="No Media"
              />
            )}
          </ListingImage>
          <ListingDetails>
            <ListingTitle>{listing.title}</ListingTitle>
            <ListingMeta>
              <span>{listing.category}</span>
              {listing.price && (
                <span>UGX {listing.price.toLocaleString()}</span>
              )}
              <StatusBadge sold={listing.sold}>
                {listing.sold ? 'Sold' : 'Available'}
              </StatusBadge>
            </ListingMeta>
          </ListingDetails>
        </ListingItem>
      ))}
    </ContentSection>
  );
};

export default Overview;